import { HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { Tierliste } from '../../../../../projects/kiene-core/src/lib/components/gruppe-tier/classes/tierliste';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneBackendApiService } from '../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { LocalStorageService } from '../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';

/** Caches kundenportal tierliste reads for dashboard row menus. */
@Injectable()
export class LetzteAktionenTierlisteMenuCacheService {
    private readonly cache = new Map<string, Observable<Tierliste>>();
    private readonly changedTierlisteIds = new Subject<number>();

    constructor(
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly api: KieneBackendApiService,
        private readonly localStorageService: LocalStorageService,
    ) {}

    prefetch(tierlisteIds: number[]): void {
        const unique = [...new Set(tierlisteIds.filter(id => id > 0))];
        for (const id of unique) {
            this.get(id).subscribe();
        }
    }

    get(tierlisteId: number): Observable<Tierliste> {
        if (tierlisteId <= 0) {
            return of(null as unknown as Tierliste);
        }
        const cacheKey = this.buildCacheKey(tierlisteId);
        let cached = this.cache.get(cacheKey);
        if (!cached) {
            let params = new HttpParams().set('tierliste_id', String(tierlisteId));
            const kundeId = Number(this.localStorageService.getCurrentClient()?.kunde_id) || 0;
            if (kundeId > 0) {
                params = params.set('kunde_id', String(kundeId));
            }
            cached = this.api.get(this.apiBaseUrl + 'tierliste/read_one_kundenportal.php', params).pipe(
                shareReplay({ bufferSize: 1, refCount: false }),
                catchError(err => {
                    this.cache.delete(cacheKey);
                    throw err;
                }),
            );
            this.cache.set(cacheKey, cached);
        }
        return cached;
    }

    invalidate(tierlisteId?: number): void {
        if (tierlisteId && tierlisteId > 0) {
            const prefix = `${tierlisteId}:`;
            for (const key of [...this.cache.keys()]) {
                if (key === String(tierlisteId) || key.startsWith(prefix)) {
                    this.cache.delete(key);
                }
            }
            return;
        }
        this.cache.clear();
    }

    /** Drop cached read and notify listeners (e.g. open detail dialog) to reload. */
    notifyChanged(tierlisteId: number): void {
        if (tierlisteId <= 0) {
            return;
        }
        this.invalidate(tierlisteId);
        this.changedTierlisteIds.next(tierlisteId);
    }

    watchChanged(): Observable<number> {
        return this.changedTierlisteIds.asObservable();
    }

    private buildCacheKey(tierlisteId: number): string {
        const kundeId = Number(this.localStorageService.getCurrentClient()?.kunde_id) || 0;
        return `${tierlisteId}:${kundeId}`;
    }
}
